import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Search, Download, Loader2, Database, Users, UserPlus, Activity, TrendingUp, ArrowUp, ArrowDown, ChevronsUpDown, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { API } from "@/lib/newsUtils";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminData() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ username: "", email: "", registered_from: "", registered_to: "" });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const numericKeys = ["sources_count"];
  const sortedRows = useMemo(() => {
    const { key, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      let va = a[key], vb = b[key];
      if (numericKeys.includes(key)) {
        va = va ?? 0; vb = vb ?? 0;
        return (va - vb) * factor;
      }
      va = (va ?? "").toString().toLowerCase();
      vb = (vb ?? "").toString().toLowerCase();
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  }, [rows, sort]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/stats`);
        setStats(data);
      } catch { /* silencioso */ }
    })();
  }, []);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const params = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  };

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { params: params() });
      setRows(data.users);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/users/export`, { params: params(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "usuarios_newsdigest.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo exportar");
    } finally {
      setExporting(false);
    }
  };

  const sendDigestsNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await axios.post(`${API}/admin/send-digests`);
      toast.success(`Resúmenes enviados: ${data.sent} correo(s).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron enviar los resúmenes");
    } finally {
      setSendingDigest(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-data">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-lg font-black tracking-tight">DATOS</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-send-digests-button"
              onClick={sendDigestsNow}
              disabled={sendingDigest}
              className="rounded-none bg-primary hover:bg-blue-900 h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
            >
              {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="hidden sm:inline">Enviar resúmenes</span>
            </Button>
            <Button
              data-testid="admin-back-button"
              variant="outline"
              onClick={() => navigate("/admin")}
              className="rounded-none border-border h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Volver</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {stats && (
          <section data-testid="admin-stats">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-3">
              Destacados · últimos 7 días
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="border border-border p-5 flex items-center gap-4" data-testid="stat-new-users">
                <div className="w-11 h-11 bg-primary/10 flex items-center justify-center shrink-0">
                  <UserPlus className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-heading text-3xl font-black leading-none">{stats.new_users}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Nuevos usuarios</p>
                </div>
              </div>
              <div className="border border-border p-5 flex items-center gap-4" data-testid="stat-active-users">
                <div className="w-11 h-11 bg-primary/10 flex items-center justify-center shrink-0">
                  <Activity className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-heading text-3xl font-black leading-none">{stats.active_users}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Usuarios activos</p>
                </div>
              </div>
              <div className="border border-border p-5 flex items-center gap-4" data-testid="stat-top-source">
                <div className="w-11 h-11 bg-primary/10 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-heading text-xl font-black leading-none truncate">
                    {stats.top_source ? stats.top_source.name : "—"}
                  </p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">
                    Medio más seguido · global{stats.top_source ? ` · ${stats.top_source.count}` : ""}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        <form onSubmit={search} className="border border-border p-5" data-testid="admin-filters">
          <h2 className="font-heading text-lg font-black tracking-tight mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" /> Buscar usuarios
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="f-user" className="text-xs uppercase tracking-wide font-semibold">Usuario</Label>
              <Input id="f-user" data-testid="filter-username" className="rounded-none mt-1"
                value={filters.username} onChange={(e) => setF("username", e.target.value)} placeholder="Nombre de usuario" />
            </div>
            <div>
              <Label htmlFor="f-email" className="text-xs uppercase tracking-wide font-semibold">Correo</Label>
              <Input id="f-email" data-testid="filter-email" className="rounded-none mt-1"
                value={filters.email} onChange={(e) => setF("email", e.target.value)} placeholder="correo@ejemplo.com" />
            </div>
            <div>
              <Label htmlFor="f-from" className="text-xs uppercase tracking-wide font-semibold">Registro desde</Label>
              <Input id="f-from" type="date" data-testid="filter-from" className="rounded-none mt-1"
                value={filters.registered_from} onChange={(e) => setF("registered_from", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="f-to" className="text-xs uppercase tracking-wide font-semibold">Registro hasta</Label>
              <Input id="f-to" type="date" data-testid="filter-to" className="rounded-none mt-1"
                value={filters.registered_to} onChange={(e) => setF("registered_to", e.target.value)} />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-5">
            <Button type="submit" data-testid="admin-search-button" disabled={loading}
              className="rounded-none bg-primary hover:bg-blue-900 h-10 gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </Button>
            <Button type="button" data-testid="admin-export-button" onClick={exportCsv}
              disabled={exporting || total === null} variant="outline"
              className="rounded-none border-border h-10 gap-2">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Exportar CSV
            </Button>
          </div>
        </form>

        {total !== null && (
          <div data-testid="admin-results">
            <p className="text-sm text-muted-foreground mb-3 uppercase tracking-widest font-medium">
              {total} usuario{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}
            </p>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-left border-b border-border">
                    <SortHeader label="Usuario" k="username" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Correo" k="email" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Nº medios" k="sources_count" sort={sort} onSort={toggleSort} align="center" />
                    <SortHeader label="Actividad" k="total_opens" sort={sort} onSort={toggleSort} align="center" />
                    <SortHeader label="Registro" k="created_at" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Último acceso" k="last_login_at" sort={sort} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground" data-testid="admin-empty">
                      No hay usuarios que coincidan con los filtros.
                    </td></tr>
                  ) : sortedRows.map((u) => (
                    <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid="admin-user-row">
                      <td className="px-4 py-3 font-semibold">{u.username}</td>
                      <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3 text-center">
                        {u.sources_count === 0 ? (
                          <span className="font-bold text-muted-foreground">0</span>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                data-testid="sources-count-trigger"
                                className="font-bold text-primary underline decoration-dotted underline-offset-4 hover:text-blue-900"
                              >
                                {u.sources_count}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 rounded-none" align="center" data-testid="sources-popover">
                              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                                Medios suscritos
                              </p>
                              <div className="flex flex-wrap gap-1 max-h-48 overflow-auto">
                                {u.sources.map((s, i) => (
                                  <span key={i} className="text-[11px] bg-muted border border-border px-2 py-0.5 uppercase tracking-wide">{s}</span>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(u.total_opens || 0) === 0 && (u.total_minutes || 0) === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                data-testid="activity-trigger"
                                className="font-bold text-primary underline decoration-dotted underline-offset-4 hover:text-blue-900 whitespace-nowrap"
                              >
                                {u.total_opens || 0} ap · {u.total_minutes || 0}m
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 rounded-none" align="center" data-testid="activity-popover">
                              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                                Medios más usados
                              </p>
                              <div className="space-y-1.5 max-h-56 overflow-auto">
                                {(u.top_media || []).length === 0 ? (
                                  <p className="text-sm text-muted-foreground">Sin actividad registrada.</p>
                                ) : (
                                  u.top_media.map((m, i) => (
                                    <div key={i} className="flex items-center justify-between gap-3 text-sm">
                                      <span className="font-medium truncate">{m.source}</span>
                                      <span className="text-muted-foreground whitespace-nowrap tabular-nums">
                                        {m.opens} ap · {m.minutes}m
                                      </span>
                                    </div>
                                  ))
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(u.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SortHeader({ label, k, sort, onSort, align = "left" }) {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 font-semibold ${align === "center" ? "text-center" : "text-left"}`}>
      <button
        type="button"
        data-testid={`sort-${k}`}
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 select-none transition-colors hover:text-primary ${active ? "text-primary" : ""} ${align === "center" ? "mx-auto" : ""}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}
